import type { FilterGroup } from 'components/SearchArea/type';

import { useState } from 'react';

import { paramToGroupId } from 'constants/searchArea';

function updateFeatureInGroups(
  groups: FilterGroup[],
  groupId: string,
  featureId: string,
  updater: (feature: any) => any,
): FilterGroup[] {
  return groups.map((group) =>
    group.groupId !== groupId
      ? group
      : {
          ...group,
          features: group.features.map((feature) =>
            feature.featureId !== featureId ? feature : updater(feature),
          ),
        },
  );
}

function applyQueryToFilterGroups(
  filterGroups: FilterGroup[],
  queryParams: Record<string, any>,
): FilterGroup[] {
  return filterGroups.map((group) => {
    const paramKey = Object.entries(paramToGroupId).find(([, gid]) => gid === group.groupId)?.[0];
    const paramValue = paramKey ? queryParams[paramKey] : [];
    const values = Array.isArray(paramValue) ? paramValue : paramValue ? [paramValue] : [];

    return {
      ...group,
      features: group.features.map((feature) => ({
        ...feature,
        selected: values.includes(feature.featureId),
      })),
    };
  });
}

export function useFilterGroups(
  initialFilterGroups: FilterGroup[],
  queryParams: Record<string, any> = {},
) {
  const filterData = applyQueryToFilterGroups(initialFilterGroups, queryParams);
  // stores the current editing state of filters (used for temporary changes before confirmation)
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(filterData);
  // stores the last confirmed filter state (used for displaying applied filters and for comparison)
  const [confirmedFilterGroups, setConfirmedFilterGroups] = useState<FilterGroup[]>(filterData);

  const setSyncedFilterData = (queryParams: Record<string, any>) => {
    const newFilterGroups = applyQueryToFilterGroups(initialFilterGroups, queryParams);

    setFilterGroups(newFilterGroups);
    setConfirmedFilterGroups(newFilterGroups);
  };

  const handleCheckboxChange = (groupId: string, featureId: string) => {
    setFilterGroups((prev) =>
      updateFeatureInGroups(prev, groupId, featureId, (feature) => ({
        ...feature,
        selected: !feature.selected,
      })),
    );
  };

  const handleClearAll = () => {
    setFilterGroups((prev) =>
      prev.map((group) => ({
        ...group,
        features: group.features.map((feature) => ({
          ...feature,
          selected: false,
        })),
      })),
    );
  };

  const handleConfirmFilters = (
    onClose: () => void,
    onConfirmed?: (groups: FilterGroup[]) => void,
  ) => {
    setConfirmedFilterGroups(filterGroups);
    if (onConfirmed) onConfirmed(filterGroups);
    onClose();
  };

  const handleCancelFilters = () => {
    setFilterGroups(confirmedFilterGroups);
  };

  return {
    filterGroups,
    confirmedFilterGroups,
    handleCheckboxChange,
    handleClearAll,
    handleConfirmFilters,
    handleCancelFilters,
    setSyncedFilterData,
  };
}
